import { supabase } from '@/lib/supabase';

/**
 * Service dédié à la création et validation des profils utilisateur
 * Principe SOLID : Single Responsibility - Responsabilité unique de garantir l'existence et la validité du profil
 * Principe KISS : Solutions simples et directes
 */
export class ProfileCreationService {
  private static readonly MAX_RETRIES = 3;
  private static readonly RETRY_DELAYS = [500, 1000, 2000]; // ms

  /**
   * Garantit que le profil utilisateur existe et a un rôle valide
   * @param userId ID de l'utilisateur
   * @returns true si le profil existe et est valide, false sinon
   */
  static async ensureProfileExists(userId: string): Promise<boolean> {
    try {
      // Vérifier si le profil existe déjà
      const { data: existingProfile, error: checkError } = await supabase
        .from('profiles')
        .select('id, role')
        .eq('id', userId)
        .single();

      // Si le profil existe et a un rôle valide, retourner true
      if (!checkError && existingProfile && existingProfile.role) {
        return true;
      }

      // Si le profil existe mais n'a pas de rôle, le corriger
      if (!checkError && existingProfile && !existingProfile.role) {
        console.warn(`Profil ${userId} existe mais n'a pas de rôle, correction en cours...`);
        const fixed = await this.validateProfileRole(userId);
        return fixed;
      }

      // Si le profil n'existe pas (erreur PGRST116), le créer
      if (checkError?.code === 'PGRST116') {
        return await this.createProfileWithRetry(userId);
      }

      // Autre erreur
      console.error('Erreur lors de la vérification du profil:', checkError);
      return false;
    } catch (error) {
      console.error('Exception dans ensureProfileExists:', error);
      return false;
    }
  }

  /**
   * Crée le profil avec retry simple
   * Principe KISS : Retry simple avec délais fixes
   */
  private static async createProfileWithRetry(userId: string): Promise<boolean> {
    for (let attempt = 0; attempt < this.MAX_RETRIES; attempt++) {
      try {
        console.log(`Tentative ${attempt + 1}/${this.MAX_RETRIES} de création du profil pour ${userId}`);
        
        const success = await this.getOrCreateProfile();
        
        if (success) {
          // Vérifier que le profil a bien été créé et a un rôle
          await new Promise(resolve => setTimeout(resolve, this.RETRY_DELAYS[attempt]));
          
          const { data: verifiedProfile, error: verifyError } = await supabase
            .from('profiles')
            .select('id, role')
            .eq('id', userId)
            .single();

          if (!verifyError && verifiedProfile && verifiedProfile.role) {
            console.log(`Profil créé avec succès`);
            return true;
          }

          // Si le profil existe mais n'a pas de rôle, le corriger
          if (!verifyError && verifiedProfile && !verifiedProfile.role) {
            console.warn(`Profil créé mais sans rôle, correction en cours...`);
            return await this.validateProfileRole(userId);
          }
        }

        // Si ce n'est pas la dernière tentative, attendre avant de réessayer
        if (attempt < this.MAX_RETRIES - 1) {
          await new Promise(resolve => setTimeout(resolve, this.RETRY_DELAYS[attempt]));
        }
      } catch (error) {
        console.error(`Erreur lors de la tentative ${attempt + 1}:`, error);
        if (attempt === this.MAX_RETRIES - 1) {
          return false;
        }
        await new Promise(resolve => setTimeout(resolve, this.RETRY_DELAYS[attempt]));
      }
    }

    console.error(`Impossible de créer le profil après ${this.MAX_RETRIES} tentatives`);
    return false;
  }

  /**
   * Appelle la fonction RPC get_or_create_profile
   * Wrapper simple autour de la fonction RPC
   * La fonction RPC utilise auth.uid() directement, donc pas besoin de passer userId
   */
  private static async getOrCreateProfile(): Promise<boolean> {
    try {
      const { data: createdProfiles, error: createError } = await supabase
        .rpc('get_or_create_profile');

      if (createError) {
        console.error('Erreur lors de l\'appel à get_or_create_profile:', createError);
        return false;
      }

      if (!createdProfiles || createdProfiles.length === 0) {
        console.warn('get_or_create_profile a retourné un tableau vide');
        return false;
      }

      return true;
    } catch (error) {
      console.error('Exception lors de l\'appel à get_or_create_profile:', error);
      return false;
    }
  }

  /**
   * Valide et corrige le rôle du profil si nécessaire
   * Garantit qu'un rôle par défaut 'renter' est toujours défini
   */
  static async validateProfileRole(userId: string): Promise<boolean> {
    try {
      // Récupérer le profil actuel
      const { data: profile, error: fetchError } = await supabase
        .from('profiles')
        .select('id, role')
        .eq('id', userId)
        .single();

      if (fetchError) {
        console.error('Erreur lors de la récupération du profil pour validation:', fetchError);
        return false;
      }

      if (!profile) {
        console.error('Profil non trouvé pour validation');
        return false;
      }

      // Si le rôle est NULL ou invalide, le définir à 'renter' par défaut
      if (!profile.role) {
        console.log(`Correction du rôle NULL pour le profil ${userId}, définition à 'renter'`);
        
        const { error: updateError } = await supabase
          .from('profiles')
          .update({ role: 'renter', updated_at: new Date().toISOString() })
          .eq('id', userId);

        if (updateError) {
          console.error('Erreur lors de la mise à jour du rôle:', updateError);
          return false;
        }

        console.log(`Rôle corrigé avec succès pour ${userId}`);
        return true;
      }

      // Le rôle existe déjà, tout est bon
      return true;
    } catch (error) {
      console.error('Exception dans validateProfileRole:', error);
      return false;
    }
  }

  /**
   * Récupère le rôle de l'utilisateur en garantissant que le profil existe
   * Méthode principale à utiliser pour obtenir le rôle
   */
  static async getUserRole(userId: string): Promise<'owner' | 'renter' | 'admin' | null> {
    try {
      // D'abord, garantir que le profil existe
      const profileExists = await this.ensureProfileExists(userId);
      
      if (!profileExists) {
        console.warn(`Impossible de garantir l'existence du profil pour ${userId}`);
        return null;
      }

      // Récupérer le rôle du profil
      const { data: profile, error } = await supabase
        .from('profiles')
        .select('role')
        .eq('id', userId)
        .single();

      if (error) {
        console.error('Erreur lors de la récupération du rôle:', error);
        return null;
      }

      if (!profile || !profile.role) {
        console.warn(`Profil ${userId} n'a pas de rôle après validation`);
        return null;
      }

      // Mapper les rôles de la base de données vers les rôles du code
      return this.mapDatabaseRoleToCodeRole(profile.role);
    } catch (error) {
      console.error('Exception dans getUserRole:', error);
      return null;
    }
  }

  /**
   * Mappe les rôles de la base de données vers les rôles du code
   * Centralise la logique de mapping
   */
  private static mapDatabaseRoleToCodeRole(
    dbRole: string
  ): 'owner' | 'renter' | 'admin' | null {
    if (dbRole === 'host' || dbRole === 'proprietaire' || dbRole === 'owner') {
      return 'owner';
    }
    if (dbRole === 'locataire' || dbRole === 'renter') {
      return 'renter';
    }
    if (dbRole === 'admin') {
      return 'admin';
    }
    return null;
  }
}

